import { useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { GitBranch, Loader2 } from "lucide-react";
import VersionControlConfig from "./VersionControlConfig.jsx";
import ProjectLifecycleConfig from "./ProjectLifecycleConfig.jsx";
import { adaiApi } from "../api/adaiApi.js";
import { getApiError } from "../../api/client";
import { LIFECYCLE_PLATFORM, VCS_PLATFORM } from "../utils/constants.js";

const POLL_INTERVAL_MS = 5000;

const STATUS_LABEL = {
  queued:    "Queued — waiting to start…",
  running:   "Generating documentation…",
  completed: "Completed",
  failed:    "Failed",
};

const AdaiForm = ({ onResult, onGenerationStart, onGenerationEnd, apiError, setApiError }) => {
  const methods = useForm({
    defaultValues: {
      diff_adapter_type: "",
      work_item_adapter_type: "",
      repository: "",
      source_version: "",
      target_version: "",
      azure_devops_org: "",
      azure_devops_project: "",
      version_control_azure_devops_org: "",
      version_control_azure_devops_project: "",
      bitbucket_email: "",
      jira_email: "",
      jira_base_url: "",
      jira_project_key: "",
      release_tag: "",
      release_date: "",
      date_from: "",
      date_to: "",
      diff_adapter_pat: "",
      work_item_adapter_pat: "",
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const intervalRef   = useRef(null);
  const [pollingStatus, setPollingStatus] = useState(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const buildGenerateDocsPayload = (data) => {
    const adapter_config = {
      work_item_adapter_type: data.work_item_adapter_type,
      work_item_adapter_pat:  data.work_item_adapter_pat?.trim() || "",
      diff_adapter_type:      data.diff_adapter_type,
      diff_adapter_pat:       data.diff_adapter_pat?.trim() || "",
      repository:             data.repository.trim(),

      azure_devops_org:
        data.work_item_adapter_type === LIFECYCLE_PLATFORM.AZURE_DEVOPS
          ? data.azure_devops_org?.trim()
          : null,
      azure_devops_project:
        data.work_item_adapter_type === LIFECYCLE_PLATFORM.AZURE_DEVOPS
          ? data.azure_devops_project?.trim()
          : null,

      version_control_azure_devops_org:
        data.diff_adapter_type === VCS_PLATFORM.AZURE_DEVOPS_GIT
          ? data.version_control_azure_devops_org?.trim()
          : null,
      version_control_azure_devops_project:
        data.diff_adapter_type === VCS_PLATFORM.AZURE_DEVOPS_GIT
          ? data.version_control_azure_devops_project?.trim()
          : null,

      bitbucket_email:
        data.diff_adapter_type === VCS_PLATFORM.BITBUCKET
          ? data.bitbucket_email?.trim()
          : null,

      jira_email:
        data.work_item_adapter_type === LIFECYCLE_PLATFORM.JIRA
          ? data.jira_email?.trim()
          : null,
      jira_base_url:
        data.work_item_adapter_type === LIFECYCLE_PLATFORM.JIRA
          ? data.jira_base_url?.trim()
          : null,
      jira_project_key:
        data.work_item_adapter_type === LIFECYCLE_PLATFORM.JIRA
          ? data.jira_project_key?.trim()
          : null,
    };

    return {
      adapter_config,
      source_version: data.source_version.trim(),
      target_version: data.target_version.trim(),
      release_tag:    data.release_tag.trim(),
      release_date:   data.release_date || undefined,
      date_from:      data.date_from    || undefined,
      date_to:        data.date_to      || undefined,
    };
  };

  const requestDocumentGeneration = async (data) => {
    setApiError("");
    onResult(null);
    setPollingStatus(null);

    let generationStarted = false;
    let hadError = false;

    try {
      const payload = buildGenerateDocsPayload(data);

      const { data: jobData } = await adaiApi.generateDocument(payload);
      const jobId = jobData.job_id;
      setPollingStatus("queued");
      generationStarted = true;
      onGenerationStart?.();

      const finalJob = await new Promise((resolve, reject) => {
        const id = setInterval(async () => {
          try {
            const { data: job } = await adaiApi.getJobStatus(jobId);
            setPollingStatus(job.status);

            if (job.status === "completed") {
              clearInterval(id);
              intervalRef.current = null;
              resolve(job);
            } else if (job.status === "failed") {
              clearInterval(id);
              intervalRef.current = null;
              reject(new Error(job.error || "Document generation failed."));
            }
          } catch (err) {
            clearInterval(id);
            intervalRef.current = null;
            reject(err);
          }
        }, POLL_INTERVAL_MS);

        intervalRef.current = id;
      });

      onResult(finalJob);
    } catch (err) {
      hadError = true;
      setApiError(getApiError(err));
    } finally {
      setPollingStatus(null);
      onGenerationEnd?.(generationStarted && hadError);
    }
  };

  const buttonLabel = isSubmitting
    ? (STATUS_LABEL[pollingStatus] ?? "Starting…")
    : "Generate Documentation";

  return (
    <div className="w-full bg-white! mb-5! rounded-4xl border border-slate-100 p-8! flex flex-col items-start! text-left!">
      <div className="mb-5! w-full! text-left!">
        <h3 className="text-lg font-bold text-slate-800 m-0! text-left!">
          Generate Release Documentation
        </h3>
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(requestDocumentGeneration)}
          className="w-full! block! space-y-2!"
        >
          <ProjectLifecycleConfig />
          <VersionControlConfig />

          {apiError && (
            <div className="p-5 py-3 bg-red-50! border border-red-100! text-red-600! rounded-2xl text-[14px] mb-8! text-left!">
              {apiError}
            </div>
          )}

          <div className="flex justify-end! w-full! border-t! border-slate-50!">
            <button
              type="submit"
              disabled={isSubmitting}
              className="h-11.5! px-8! rounded-xl! bg-[#1d4ed8]! text-white! font-semibold text-sm flex items-center gap-2 hover:bg-[#1e40af]! transition-all active:scale-[0.98] disabled:opacity-50 border-0! cursor-pointer shadow-sm"
            >
              {isSubmitting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <GitBranch size={20} />
              )}
              {buttonLabel}
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AdaiForm;
